// Owns the lifecycle of the comparison swipe: a second maplibre-gl map ("before"
// map) overlaid on the main map, with a `@maplibre/maplibre-gl-compare` control
// wired between them. Toggled by the `enabled` flag.
//
// All async work is guarded against re-entry — if the user disables comparison
// while we're mid-await, the cleanup ref short-circuits the rest of init. The
// before map mirrors the main map's interaction-handler state (pan/zoom locks)
// so the wizard's per-step locks can't be bypassed by dragging the left half.

import { useEffect, useRef, type RefObject } from "react";
import maplibregl from "maplibre-gl";
import { registerTileBoundaryToggle } from "@/lib/maplibre-tile-overlay";
import {
  buildParcelAttribution,
} from "../utils/parcel-attribution";
import { queryLayerDataForParcel } from "../utils/geojson-cache";
import {
  getMaptilerStyleUrl,
  getPlaceholderStyle,
  MAPTILER_STYLE_KEYS,
  type MaptilerStyleKey,
} from "@/components/basemap/styles";
import { registerBeforeMapLayers } from "../map/register-before-map-layers";
import { syncBeforeMapLayers } from "../map/apply-layer-state";
import type {
  DemoLayerStates,
  ParcelAttribution,
} from "../types/agricultural-lulc";

export interface UseComparisonMapArgs {
  enabled: boolean;
  mainMap: RefObject<maplibregl.Map | null>;
  beforeMapContainer: RefObject<HTMLDivElement | null>;
  compareContainer: RefObject<HTMLDivElement | null>;
  // Latest layer state — read live via a ref inside the hook so the async
  // setup can't capture a stale snapshot.
  layers: DemoLayerStates;
  // Basemap style key mounted on the main map; the before map matches it.
  currentStyleRef: RefObject<MaptilerStyleKey | null>;
  fallbackStyleId: MaptilerStyleKey;
  onParcelSelected: (attribution: ParcelAttribution) => void;
  // Stable highlight ref so the click handler can never go stale even though
  // it's only attached once at before-map creation.
  onParcelHighlight: (pid: string) => void;
}

export interface UseComparisonMapResult {
  beforeMap: RefObject<maplibregl.Map | null>;
}

const waitForFrame = () =>
  new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));

export function useComparisonMap(args: UseComparisonMapArgs): UseComparisonMapResult {
  const {
    enabled,
    mainMap,
    beforeMapContainer,
    compareContainer,
    layers,
    currentStyleRef,
    fallbackStyleId,
    onParcelSelected,
    onParcelHighlight,
  } = args;

  const beforeMap = useRef<maplibregl.Map | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const compareControl = useRef<any>(null);
  const unregisterTileOverlay = useRef<(() => void) | null>(null);

  // Live handles for state/callbacks the click handler closes over. The bm
  // click handler is only attached once at create time, so anything that can
  // change later has to come through a ref.
  const layersRef = useRef<DemoLayerStates>(layers);
  layersRef.current = layers;
  const onParcelSelectedRef = useRef(onParcelSelected);
  onParcelSelectedRef.current = onParcelSelected;
  const onParcelHighlightRef = useRef(onParcelHighlight);
  onParcelHighlightRef.current = onParcelHighlight;

  // ─── Enable/disable ──────────────────────────────────────────────
  useEffect(() => {
    if (!enabled) {
      teardown();
      return;
    }
    void enable();
    return teardown;
    // Driven by `enabled` alone. Container/main-map refs are stable; layer
    // state and callbacks are reached via refs so we don't tear-down on every
    // change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  // ─── Live sync of before-map layer visibility ────────────────────
  // Without this, toggling Forest/Crops/Parcels in the panel only updates the
  // right (main) map.
  useEffect(() => {
    const bm = beforeMap.current;
    if (!enabled || !bm) return;
    syncBeforeMapLayers(bm, layers);
  }, [layers, enabled]);

  return { beforeMap };

  // ─── Helpers ─────────────────────────────────────────────────────

  async function enable() {
    const m = mainMap.current;
    const container = compareContainer.current;
    const beforeEl = beforeMapContainer.current;

    if (!m || !beforeEl || !container || beforeMap.current) return;

    // Dynamically import @maplibre/maplibre-gl-compare (CJS module)
    let Compare: typeof import("@maplibre/maplibre-gl-compare").default;
    try {
      const compareModule = await import("@maplibre/maplibre-gl-compare");
      Compare = compareModule.default || compareModule;
    } catch (err) {
      console.error("[lulc-demo] failed to load @maplibre/maplibre-gl-compare:", err);
      return;
    }

    beforeEl.style.visibility = "visible";
    beforeEl.style.pointerEvents = "auto";
    await waitForFrame();

    const rect = beforeEl.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;

    const center = m.getCenter();
    const zoom = m.getZoom();
    const bearing = m.getBearing();
    const pitch = m.getPitch();
    const styleId = currentStyleRef.current || fallbackStyleId;

    // Placeholder style first so the before map mounts immediately (the compare
    // control needs both maps in the DOM) without flashing a default basemap.
    let bm: maplibregl.Map;
    try {
      bm = new maplibregl.Map({
        container: beforeEl,
        style: getPlaceholderStyle(styleId === MAPTILER_STYLE_KEYS.DARK),
        center: [center.lng, center.lat],
        zoom,
        bearing,
        pitch,
      });
      beforeMap.current = bm;
      // Backtick toggles the tile-boundary overlay on this map too.
      unregisterTileOverlay.current = registerTileBoundaryToggle(bm);
    } catch (err) {
      console.error("[lulc-demo] failed to construct before map:", err);
      return;
    }

    bm.setStyle(getMaptilerStyleUrl(styleId));

    // Mirror interaction-handler state so wizard pan/zoom locks (notably
    // step 7) can't be bypassed by dragging the left half — @maplibre/maplibre-gl-compare
    // syncs both cameras, so any movement on either side moves both.
    if (!m.dragPan.isEnabled()) bm.dragPan.disable();
    if (!m.scrollZoom.isEnabled()) bm.scrollZoom.disable();
    if (!m.doubleClickZoom.isEnabled()) bm.doubleClickZoom.disable();
    if (!m.keyboard.isEnabled()) bm.keyboard.disable();
    if (!m.touchZoomRotate.isEnabled()) bm.touchZoomRotate.disable();

    await new Promise<void>((resolve) => {
      bm.on("load", () => resolve());
    });

    // Bail if disabled while we were waiting
    if (!beforeMap.current) return;

    try {
      await registerBeforeMapLayers(bm);

      // Mirror the main map's parcel click + cursor handlers onto the before
      // map so the left half of the swipe is interactive too. All callbacks
      // dispatched via refs so this handler can't capture stale identities.
      bm.on("click", "parcels-fill", (e) => {
        const feature = e.features?.[0];
        if (!feature) return;
        const pid = feature.properties?.cadastre_polygon_pid;
        const areaSqm = feature.properties?.area;
        if (pid) {
          const layerData = queryLayerDataForParcel(pid);
          const attribution = buildParcelAttribution(pid, areaSqm ?? 0, layerData);
          onParcelSelectedRef.current(attribution);
          onParcelHighlightRef.current(pid);
        }
      });
      bm.on("mouseenter", "parcels-fill", () => {
        bm.getCanvas().style.cursor = "pointer";
      });
      bm.on("mouseleave", "parcels-fill", () => {
        bm.getCanvas().style.cursor = "";
      });

      syncBeforeMapLayers(bm, layersRef.current);
    } catch (err) {
      console.error("[lulc-demo] failed to add before-map layers:", err);
      return;
    }

    if (!beforeMap.current) return;

    try {
      compareControl.current = new Compare(bm, m, container, {});
    } catch (err) {
      console.error("[lulc-demo] failed to create Compare control:", err);
    }
  }

  function teardown() {
    if (compareControl.current) {
      compareControl.current.remove();
      compareControl.current = null;
    }
    if (unregisterTileOverlay.current) {
      unregisterTileOverlay.current();
      unregisterTileOverlay.current = null;
    }
    if (beforeMap.current) {
      beforeMap.current.remove();
      beforeMap.current = null;
    }
    if (beforeMapContainer.current) {
      beforeMapContainer.current.style.visibility = "hidden";
      beforeMapContainer.current.style.pointerEvents = "none";
    }
  }
}
